import { PRODUCT_TITLES } from './white.js';
import { PANEL_STYLES } from './componentHelper';
import { keys } from 'lodash';

export const NAME = 'scenarios';
export const KPPJOB_TYPE = 'kppJob';

export const NEW_SCENARIOS = `${NAME}/NEW`;

export const WS_SCENARIO_INFO = 'scenarioInfo';

export const NO_REASON_FOUND = 'No reason found';

export const SCENARIO_COMPLETED = 'COMPLETED';
export const SCENARIO_FAILED = 'FAILED';
export const SCENARIO_DISPATCHED = 'DISPATCHED';
export const SCENARIO_RUNNING = 'RUNNING';
export const SCENARIO_SKIPPED = 'SKIPPED';
export const SCENARIO_ERROR = 'ERROR';
export const SCENARIO_CANCELLED = 'CANCELLED';
export const SCENARIO_UNKNOWN = 'UNKNOWN';
export const SCENARIO_NA = 'N/A';
export const SCENARIO_RETRIGGERED = 'RETRIGGERED';
export const TRENDING = 'TRENDING';
export const TRENDING_FAILURE = 'TRENDING_FAILED';

// Case Metrics Custom
export const SCENARIO_SCANNING_CCF = 'SCANNING_CCF';
export const SCENARIO_SCANNING_NCF = 'SCANNING_NCF';
export const SCENARIO_SETTING_UP = 'SETTING_UP';

export const SCENARIO_IMPORTANCE = [
  SCENARIO_ERROR,
  SCENARIO_FAILED,
  SCENARIO_RUNNING,
  SCENARIO_DISPATCHED,
  SCENARIO_COMPLETED,
  SCENARIO_RETRIGGERED,
  SCENARIO_SKIPPED,
  SCENARIO_CANCELLED,
  SCENARIO_UNKNOWN
];

export const SCENARIO_STATUS_ORDER_BY_POSITION = {
  1: 'Failed',
  2: 'Error',
  3: 'Running',
  4: 'Skipped',
  5: 'Pending',
  6: 'Passed',
  7: 'Cancelled',
  8: 'Unknown'
};

export const SCENARIO_STATUS_ORDER_BY_STATUS = {
  'Failed': 1,
  'Error': 2,
  'Running': 3,
  'Skipped': 4,
  'Pending': 5,
  'Passed': 6,
  'Cancelled': 7,
  'Unknown': 8
};

export const SCENARIO_STATE_INFO = {
  [SCENARIO_COMPLETED]: {
    label: 'Passed',
    colour: 'blue',
    glyph: 'ok'
  },
  [SCENARIO_FAILED]: {
    label: 'Failed',
    colour: 'red',
    glyph: 'remove'
  },
  [SCENARIO_DISPATCHED]: {
    label: 'Pending',
    colour: 'grey',
    glyph: 'time'
  },
  [SCENARIO_SKIPPED]: {
    label: 'Skipped',
    colour: 'orange',
    glyph: 'fast-forward'
  },
  [SCENARIO_RUNNING]: {
    label: 'Running',
    colour: 'green',
    glyph: 'play'
  },
  [SCENARIO_ERROR]: {
    label: 'Error',
    colour: 'black',
    glyph: 'flag'
  },
  [SCENARIO_UNKNOWN]: {
    label: 'Unknown Validity',
    colour: 'beige',
    glyph: 'question-sign'
  },
  [SCENARIO_NA]: {
    label: 'N/A',
    colour: 'grey',
    glyph: 'minus'
  },
  [SCENARIO_CANCELLED]: {
    label: 'Cancelled',
    colour: 'purple',
    glyph: 'minus'
  },
  [SCENARIO_RETRIGGERED]: {
    label: 'Retriggered',
    colour: 'yellow',
    glyph: 'play'
  }
};

const STATUSES_NOT_COMPLETE = [
  SCENARIO_DISPATCHED,
  SCENARIO_RUNNING,
  SCENARIO_CANCELLED
];

export const isScenarioComplete = (scenarioStatus) => {
  return !STATUSES_NOT_COMPLETE.includes(scenarioStatus);
};

const getSubValue = (name, separator, before) => {
  if (!name || typeof name !== 'string') {
    return undefined;
  }

  const colonIdx = name.indexOf(separator);
  if (colonIdx === -1) {
    return undefined;
  }

  return before ?
    name.substr(0, colonIdx) :
    name.substr(colonIdx + separator.length);
};

export const getArtScenario = name => getSubValue(name, ':', false);
export const getArtPlatform = name => getSubValue(name, ':', true);
export const getDiffScenario = name => getSubValue(name, ' - ', false);
export const getDiffType = name => getSubValue(name, ' - ', true);
export const getUiScenario = name => getSubValue(name, '-', false) ||
  getSubValue(name, ' ', false);
export const getUiProduct = name => getSubValue(name, '-', true) ||
  getSubValue(name, ' ', true);
export const getExportScenario = name => getSubValue(name, '-', false);
export const getExportType = name => getSubValue(name, '-', true);

export const getCaseAmendingJobType = name => {
  if (!name || typeof name !== 'string') {
    return undefined;
  }

  const hyphenIdx = name.indexOf('-');
  if (hyphenIdx === -1) {
    return undefined;
  }

  return name.substr(0, hyphenIdx);
};

export const getCaseAmendingImage = name => {
  if (!name || typeof name !== 'string') {
    return undefined;
  }

  const hyphenIdx = name.indexOf('-');
  if (hyphenIdx === -1) {
    return undefined;
  }

  return name.substr(hyphenIdx + 1);
};

export const getArtifactPlatform = name => {
  if (!name || typeof name !== 'string') {
    return undefined;
  }

  const hyphenIdx = name.indexOf(':');
  if (hyphenIdx === -1) {
    return undefined;
  }

  return name.substr(0, hyphenIdx);
};

export const getScenarioJobs = (scenarios, jobs) => {
  return scenarios.map(s => jobs[s.runner])
    .filter(j => j !== undefined && j !== null)
    .reduce((current, next) => {
      current[next.id] = next;
      return current;
    },
    {});
};

export const getArtifactScenarioName = name => {
  if (!name || typeof name !== 'string') {
    return undefined;
  }

  const hyphenIdx = name.indexOf(':');
  if (hyphenIdx === -1) {
    return undefined;
  }

  return name.substr(hyphenIdx + 1);
};

// Needed for Jenkins parameter
export const getConvertedArtifactScenarioName = name => {
  name = getArtifactScenarioName(name);
  return name.replace('/', '%');
};

export const getJobTypes = (scenarios) => {
  if (!scenarios) {
    return;
  }

  const jobTypes = Array.from(scenarios).map(s => {
    const index = s.name.indexOf('-');
    return s.name.slice(0, index);
  });

  return jobTypes;
};

export const getStateCounts = (scenarios, predicate) => {
  const counts = new Array(Object.keys(SCENARIO_IMPORTANCE).length).fill(0);

  if (!scenarios) {
    return counts;
  }

  scenarios.forEach(scenario => {
    if (predicate && !predicate(scenario)) {
      return;
    }

    if (!scenario.INSTALL_SUCCESS) {
      counts[SCENARIO_IMPORTANCE.indexOf(scenario.status)]++;
    }

    else if (scenario.INSTALL_SUCCESS === 'Pass') {
      counts[SCENARIO_IMPORTANCE.indexOf(SCENARIO_COMPLETED)]++;
    }
    else if (scenario.INSTALL_SUCCESS === 'Fail') {
      counts[SCENARIO_IMPORTANCE.indexOf(SCENARIO_FAILED)]++;
    }
  });

  return counts;
};

export const getStateCountsFromSummary = (scenarioSummary) => {
  const counts = new Array(Object.keys(SCENARIO_IMPORTANCE).length).fill(0);
  if (keys(scenarioSummary).length === 0) {
    return counts;
  }
  keys(scenarioSummary).forEach(status =>
    counts[SCENARIO_IMPORTANCE.indexOf(status)] = scenarioSummary[status]);
  return counts;
};

export const getStatePercentages = (scenarioCountsSummary, totalCounts) =>
{
  if (!totalCounts)
  {
    return {};
  }

  const percentageSummary = {};
  keys(scenarioCountsSummary).forEach(statusIndex =>
  {
    const status = SCENARIO_IMPORTANCE[statusIndex];
    if (status && scenarioCountsSummary[statusIndex] !== 0)
    {
      percentageSummary[status] = {};
      percentageSummary[status]['percentage'] =
       ((scenarioCountsSummary[statusIndex] / totalCounts) * 100).toFixed(0) + '%';
      percentageSummary[status]['count'] = scenarioCountsSummary[statusIndex];
    }
  }
  );
  return percentageSummary;
};

export const getPieSlices = (states, colours) => {
  return states.map(si => {
    const info = SCENARIO_STATE_INFO[si];
    const colour = colours[info.colour];

    return {
      label: info.label,
      colour
    };
  });
};

export const getPanelStyle = scenarios => {
  const counts = getStateCounts(scenarios);

  let i = 0;
  for (; i < SCENARIO_IMPORTANCE.length; i++) {
    if (counts[i] > 0) {
      break;
    }
  }

  switch (SCENARIO_IMPORTANCE[i]) {
    case SCENARIO_ERROR:
      return PANEL_STYLES.DANGER;

    case SCENARIO_FAILED:
      return PANEL_STYLES.DANGER;

    case SCENARIO_RUNNING:
      return PANEL_STYLES.SUCCESS;

    case SCENARIO_DISPATCHED:
      return PANEL_STYLES.DEFAULT;

    case SCENARIO_COMPLETED:
      return PANEL_STYLES.PRIMARY;

    case SCENARIO_SKIPPED:
      return PANEL_STYLES.WARNING;

    case SCENARIO_CANCELLED:
      return PANEL_STYLES.DEFAULT;

    default:
      return PANEL_STYLES.WARNING;
  }
};

export const scenariosByImageName = 'imageName';
export const getScenariosByImageName = scenarios =>
{
  if (!scenarios) {
    return;
  }
  return scenarios.reduce((result, s) => {
    let imageName = s.name;
    if (!s.name.startsWith('ER_SCAN-') && !s.name.startsWith('SCAN-'))
    {
      const index = s.name.indexOf('-');
      imageName = s.name.slice(index + 1);
    }

    if (!result[imageName]) {
      result[imageName] = [s];
    } else {
      result[imageName].push(s);
    }

    return result;
  }, {});
};

export const scenariosByInstaller = 'installer';

export const scenariosByJobType = 'jobType';
export const getScenariosByJobType = scenarios =>
{
  if (!scenarios) {
    return;
  }
  return scenarios.reduce((result, s) => {
    let index = s.name.indexOf('-');
    if (index === -1)
      index = s.name.indexOf(' ');
    const jobType = s.name.slice(0, index);

    if (!result[jobType]) {
      result[jobType] = [s];
    } else {
      result[jobType].push(s);
    }

    return result;
  }, {});
};

export const scenariosByPlatform = 'platform';
export const getScenariosByPlatform = scenarios =>
{
  if (!scenarios) {
    return;
  }
  return scenarios.reduce((result, s) => {
    const index = s.name.indexOf(':');
    const platform = s.name.slice(0, index);

    if (!result[platform]) {
      result[platform] = [s];
    } else {
      result[platform].push(s);
    }

    return result;
  }, {});
};

export const getWhitePieStatsTitle = scenarios =>
{
  const result = [];
  if (!scenarios) {
    return;
  }

  const keys = Object.keys(scenarios);

  for (let i = 0; i < keys.length; i++) {
    const title = PRODUCT_TITLES[keys[i]];

    if (title === undefined)
    {
      result.push('');
      continue;
    }

    result.push(title);
  }
  return result;
};


export const getPieStatsTitle = scenarios =>
{
  const result = [];
  if (!scenarios) {
    return;
  }

  const keys = Object.keys(scenarios);

  for (let i = 0; i < keys.length; i++) {
    const title = keys[i];
    result.push(title);
  }
  return result;
};


// WEBPACK FOOTER //
// src/helpers/scenarios.js